import React, { useState, useRef, useEffect } from "react";
import Footer from "./Footer";
import SessionLimitModal from "./SessionLimitModal";
import { isLoginHSProduct } from "../config/product";

// ──────────────────────────────────────────────
// Login.jsx — docuai 레거시 로그인 포팅
// 이메일 + OTP 인증 로그인 (레거시 docuai.tlogin.net 동일)
// Google / Naver / Kakao 소셜 로그인
// ──────────────────────────────────────────────

// loginHS: loginhs.co.kr(홈페이지)는 로그인을 업무 도메인(loginhs.tlogin.net)에서 처리한다.
//   AUIGrid 라이선스/그리드가 loginhs.tlogin.net 에 묶여 있어, 로그인·앱은 거기서 동작해야 함.
//   → .co.kr 로그인 진입 시 .tlogin.net 으로 바운스(SNS·이메일 모두 거기서 로그인→착지).
const LOGINHS_WORK_ORIGIN = "https://loginhs.tlogin.net";
function isLoginhsHomepageHost() {
  try {
    const h = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
    return h === "loginhs.co.kr" || h === "www.loginhs.co.kr";
  } catch (_e) {
    return false;
  }
}

const Login = ({ API_BASE_URL, onLoginSuccess, onNavigate }) => {
  const [step, setStep] = useState("login"); // 'login' | 'auth'
  const [isCompany, setIsCompany] = useState(false);
  // 자동 로그인 동의 시 기억해 둔 이메일을 pre-fill (다음 방문 시 입력 생략)
  const [userEmail, setUserEmail] = useState(
    () => localStorage.getItem("savedEmail") || "",
  );
  const [userEmailValid, setUserEmailValid] = useState(() =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
      localStorage.getItem("savedEmail") || "",
    ),
  );
  const [groupId, setGroupId] = useState("");
  const [authNum, setAuthNum] = useState("");
  const [password, setPassword] = useState("");
  const [otpMode, setOtpMode] = useState(false); // false = PW 로그인 (기본, CielHS 풍), true = OTP fallback
  const [pwSetup, setPwSetup] = useState(null); // { token, user } — OTP 로그인 후 PW 미설정 사용자 PW 설정 단계
  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [showPwLogin, setShowPwLogin] = useState(false); // 로그인 PW input 보기/숨기기
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // 기본 체크
  const [resendCooldown, setResendCooldown] = useState(0); // OTP 재전송 cooldown (초)
  const [resendInfo, setResendInfo] = useState(""); // 재전송 성공 안내
  // 비밀번호 재설정 (이메일 OTP) — step "reset"
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [resetOtp, setResetOtp] = useState("");
  const [resetPw1, setResetPw1] = useState("");
  const [resetPw2, setResetPw2] = useState("");
  const [infoMsg, setInfoMsg] = useState(""); // 재설정 성공 등 안내 (login 화면 녹색)
  // SNS 로그인 — 서버에 키가 구성된 provider 만 버튼 표시 (미구성 클릭 → 깨짐 방지)
  const [oauthProviders, setOauthProviders] = useState(null); // {google,naver,kakao} | null(조회중)
  // 뷰포트 추적 — 고정 푸터(position:fixed)가 세로 가운데 정렬 카드의 하단을 가리는 문제 대응.
  //   너비뿐 아니라 높이도 추적: 폭은 넓지만 세로가 짧은 단말(태블릿 가로/분할화면)도 동일 증상.
  const [vp, setVp] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1024,
    h: typeof window !== "undefined" ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = vp.w <= 600; // 폰 (좁은 폭)
  const isTablet = vp.w > 600 && vp.w <= 1024; // 태블릿
  // 세로 공간이 부족하면(폰 또는 짧은 화면/가로모드) 카드를 상단 정렬 + 스크롤 + 푸터 여백 확보.
  const constrained = isMobile || vp.h <= 760;

  // loginHS .co.kr(홈페이지) 로그인 진입 → 업무 도메인(.tlogin.net)으로 바운스.
  //   API 키 발급 의도(apikeys_after_login)는 sessionStorage 가 도메인 간 공유되지 않으므로
  //   URL(?page=api-keys)로 릴레이 — tlogin.net 착지 후 발급 페이지가 로그인 유도→복귀를 이어감.
  useEffect(() => {
    if (isLoginhsHomepageHost()) {
      const back = sessionStorage.getItem("apikeys_after_login")
        ? "/?page=api-keys"
        : "/";
      window.location.replace(`${LOGINHS_WORK_ORIGIN}${back}`);
    }
  }, []);

  // 활성 SNS provider 조회 + OAuth 콜백 에러(?error=) 표시
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/auth/oauth-providers`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) =>
        setOauthProviders(
          d?.providers || { google: true, naver: true, kakao: true }, // 구버전 백엔드 호환
        ),
      )
      .catch(() =>
        setOauthProviders({ google: true, naver: true, kakao: true }),
      );
    try {
      const u = new URL(window.location.href);
      const errCode = u.searchParams.get("error");
      if (errCode) {
        const map = {
          session_limit:
            "동시 접속 한도 초과로 SNS 로그인에 실패했습니다. 비밀번호 로그인으로 기존 세션을 정리한 뒤 다시 시도해주세요.",
          auth_failed: "SNS 로그인에 실패했습니다. 다시 시도해주세요.",
          google_auth_failed: "구글 로그인에 실패했습니다. 다시 시도해주세요.",
          naver_auth_failed: "네이버 로그인에 실패했습니다. 다시 시도해주세요.",
          kakao_auth_failed: "카카오 로그인에 실패했습니다. 다시 시도해주세요.",
          google_not_configured: "구글 로그인은 아직 사용할 수 없습니다.",
          naver_not_configured: "네이버 로그인은 아직 사용할 수 없습니다.",
          kakao_not_configured: "카카오 로그인은 아직 사용할 수 없습니다.",
        };
        setError(map[errCode] || "로그인에 실패했습니다.");
        u.searchParams.delete("error");
        window.history.replaceState({}, "", u.toString());
      }
    } catch (e) {
      /* URL 파싱 실패 무시 */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OTP 재전송 cooldown tick
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // OTP 재전송
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || loading || !userEmail) return;
    setError("");
    setResendInfo("");
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, type: "로그인" }),
      });
      const data = await res.json();
      if (data.result || data.success) {
        setAuthNum("");
        setResendInfo("새 인증번호를 전송했습니다. 메일을 확인해 주세요.");
        setResendCooldown(30); // 30초 대기
      } else {
        setError(data.message || "인증번호 재전송에 실패했습니다.");
      }
    } catch (e) {
      setError("인증번호 재전송 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 동시접속 한도 초과 시 표시할 세션 리스트
  const [sessionLimit, setSessionLimit] = useState(null); // { endpoint, body, activeSessions }

  const userEmailRef = useRef(null);
  const groupIdRef = useRef(null);

  // ── 세션 한도 모달: 선택한 세션 끊고 재로그인 ──
  const handleSessionLimitProceed = async (revokeSessionIds) => {
    if (!sessionLimit) return;
    const { endpoint, body } = sessionLimit;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, revokeSessionIds }),
      });
      const data = await res.json();
      if (data.success) {
        setSessionLimit(null);
        if (data.deviceTrustToken) {
          localStorage.setItem("deviceTrustToken", data.deviceTrustToken);
        }
        // Codex Bug 3 fix — session-limit retry 후도 PW 미설정 처리
        if (data.needsPasswordSetup) {
          setPwSetup({ token: data.token, user: data.user });
          return;
        }
        onLoginSuccess && onLoginSuccess(data.user, data.token);
      } else if (res.status === 409 && data.code === "SESSION_LIMIT") {
        setSessionLimit({
          endpoint,
          body,
          activeSessions: data.activeSessions || [],
        });
        throw new Error("아직 세션이 남아있습니다. 추가로 끊어주세요.");
      } else {
        throw new Error(data.message || "로그인에 실패했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailInput = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setUserEmailValid(emailRegex.test(value));
    setUserEmail(value);
  };

  // 인증코드 발송 (개인) or 바로 로그인 (기업)
  const handleAuthEmail = async () => {
    setError("");

    // 자동 로그인 동의 시 이메일 기억(다음 방문 pre-fill). 미동의면 삭제.
    // ⚠️ 비밀번호는 평문 저장 안 함(XSS 위험) — 브라우저 비밀번호 관리자 + 세션 토큰 자동로그인 사용.
    if (rememberMe && userEmail.trim()) {
      localStorage.setItem("savedEmail", userEmail.trim());
    } else if (!rememberMe) {
      localStorage.removeItem("savedEmail");
    }

    if (isCompany) {
      // 기업 모드: 업체코드 + 이메일(아이디)로 바로 로그인
      if (!groupId.trim()) {
        setError("업체코드를 입력해주세요.");
        return;
      }
      if (!userEmail.trim()) {
        setError("사용자 아이디를 입력해주세요.");
        return;
      }

      try {
        setLoading(true);
        const body = {
          groupCode: groupId.trim(),
          userId: userEmail.trim(),
          password: "",
          rememberMe,
        };
        const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success) {
          onLoginSuccess && onLoginSuccess(data.user, data.token);
        } else if (res.status === 409 && data.code === "SESSION_LIMIT") {
          // 동시접속 한도 → 모달 표시
          setSessionLimit({
            endpoint: "/api/auth/login",
            body,
            activeSessions: data.activeSessions || [],
          });
        } else {
          setError(data.message || "로그인에 실패했습니다.");
        }
      } catch (e) {
        setError("로그인 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    } else {
      // 개인 모드: PW 로그인 우선 (CielHS 풍, 사용자 편의). PW 비어있거나 otpMode 면 OTP fallback.
      if (!userEmailValid) {
        setError("올바른 이메일 형식이 아닙니다.");
        return;
      }

      // PW 로그인 (개인) — 비밀번호 필수. 비밀번호 없는 사용자는 아래 '인증코드로 로그인' 으로 진입해 설정.
      if (!otpMode) {
        if (!password.trim()) {
          setError(
            "비밀번호를 입력하세요. 비밀번호가 없으면 아래 '인증코드로 로그인' 으로 진입해 설정하세요.",
          );
          return;
        }
        if (password.length < 8) {
          setError("비밀번호는 8자 이상이어야 합니다.");
          return;
        }
        try {
          setLoading(true);
          const body = { email: userEmail.trim(), password, rememberMe };
          const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (data.success) {
            onLoginSuccess && onLoginSuccess(data.user, data.token);
          } else if (res.status === 409 && data.code === "SESSION_LIMIT") {
            setSessionLimit({
              endpoint: "/api/auth/login",
              body,
              activeSessions: data.activeSessions || [],
            });
          } else if (data.code === "PASSWORD_SETUP_REQUIRED") {
            setError(
              "비밀번호가 설정되지 않은 계정입니다. 아래 '인증코드로 로그인' 으로 진입 후 설정해주세요.",
            );
            setOtpMode(true);
          } else {
            setError(data.message || "로그인에 실패했습니다.");
          }
        } catch (e) {
          setError("로그인 중 오류가 발생했습니다.");
        } finally {
          setLoading(false);
        }
        return;
      }

      // OTP fallback: deviceTrustToken 확인 → 있으면 OTP 스킵
      const deviceTrustToken = localStorage.getItem("deviceTrustToken");
      if (deviceTrustToken) {
        // 🔓 기기 신뢰 토큰으로 OTP 없이 로그인 시도
        try {
          setLoading(true);
          const body = {
            email: userEmail,
            deviceTrustToken,
            rememberMe,
          };
          const res = await fetch(`${API_BASE_URL}/api/auth/login-trusted`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (data.success) {
            console.log("🔓 기기 신뢰 로그인 성공 (OTP 스킵)");
            // Codex Bug 2 fix — trusted-login 후도 PW 미설정 → PW 설정 화면
            if (data.needsPasswordSetup) {
              setPwSetup({ token: data.token, user: data.user });
              return;
            }
            onLoginSuccess && onLoginSuccess(data.user, data.token);
            return;
          } else if (res.status === 409 && data.code === "SESSION_LIMIT") {
            setSessionLimit({
              endpoint: "/api/auth/login-trusted",
              body,
              activeSessions: data.activeSessions || [],
            });
            return;
          } else {
            // 기기 신뢰 만료 또는 이메일 불일치 → OTP 진행
            if (data.emailMismatch) {
              console.log(
                "다른 계정 로그인 시도 → OTP 진행 (deviceTrustToken 유지)",
              );
            } else {
              console.log("🔓 기기 신뢰 만료 → OTP 진행");
              localStorage.removeItem("deviceTrustToken");
            }
          }
        } catch (e) {
          console.error("기기 신뢰 로그인 실패:", e);
        } finally {
          setLoading(false);
        }
      }

      // OTP 발송
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: userEmail, type: "로그인" }),
        });
        const data = await res.json();
        if (data.result || data.success) {
          setStep("auth");
        } else {
          setError(data.message || "인증번호 전송에 실패했습니다.");
        }
      } catch (e) {
        setError("인증번호 전송 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }
  };

  // OTP 인증 후 로그인
  const handleOtpLogin = async () => {
    setError("");
    if (authNum.length < 6) {
      setError("6자리 인증번호를 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      const body = { email: userEmail, otp: authNum, rememberMe };
      const res = await fetch(`${API_BASE_URL}/api/auth/login-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        // 🔐 기기 신뢰 토큰 저장 (30일 — 같은 브라우저에서 OTP 재입력 방지)
        if (data.deviceTrustToken) {
          localStorage.setItem("deviceTrustToken", data.deviceTrustToken);
          console.log("🔐 기기 신뢰 토큰 저장 완료 (30일)");
        }
        // Phase 4 — PW 미설정 사용자 → PW 설정 화면 (편의성: 다음부터 PW 로그인)
        if (data.needsPasswordSetup) {
          setPwSetup({ token: data.token, user: data.user });
          return;
        }
        onLoginSuccess && onLoginSuccess(data.user, data.token);
      } else if (res.status === 409 && data.code === "SESSION_LIMIT") {
        setSessionLimit({
          endpoint: "/api/auth/login-otp",
          body,
          activeSessions: data.activeSessions || [],
        });
      } else {
        setError(data.message || "인증에 실패했습니다.");
      }
    } catch (e) {
      setError("로그인 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const snsAuthBase = () =>
    isLoginhsHomepageHost() ? LOGINHS_WORK_ORIGIN : API_BASE_URL;
  const handleGoogleLogin = () => {
    window.location.href = `${snsAuthBase()}/api/auth/google`;
  };
  const handleNaverLogin = () => {
    window.location.href = `${snsAuthBase()}/api/auth/naver`;
  };
  const handleKakaoLogin = () => {
    window.location.href = `${snsAuthBase()}/api/auth/kakao`;
  };

  // ── 비밀번호 재설정 (이메일 OTP) ──
  const handleSendResetOtp = async () => {
    if (!userEmail || !userEmailValid) {
      setError("이메일을 먼저 입력해주세요.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, type: "비밀번호 재설정" }),
      });
      const data = await res.json();
      if (data.success) {
        setResetOtpSent(true);
        setResendInfo("인증번호를 이메일로 보냈습니다. (5분 유효)");
      } else {
        setError(data.message || "인증번호 발송에 실패했습니다.");
      }
    } catch (e) {
      setError("인증번호 발송 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetOtp) {
      setError("인증번호를 입력해주세요.");
      return;
    }
    if (!resetPw1 || resetPw1.length < 8) {
      setError("새 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (resetPw1 !== resetPw2) {
      setError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: userEmail,
          otp: resetOtp,
          newPassword: resetPw1,
          newPasswordConfirm: resetPw2,
        }),
      });
      const data = await res.json();
      if (data.success) {
        // 재설정 완료 — 로그인 화면으로 복귀 + 안내. (백엔드가 잠금도 해제)
        setStep("login");
        setOtpMode(false);
        setResetOtpSent(false);
        setResetOtp("");
        setResetPw1("");
        setResetPw2("");
        setPassword("");
        setInfoMsg("비밀번호가 재설정되었습니다. 새 비밀번호로 로그인하세요.");
      } else {
        setError(data.message || "재설정에 실패했습니다.");
      }
    } catch (e) {
      setError("재설정 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // ── 비밀번호 재설정 화면 ──
  const renderPasswordReset = () => (
    <>
      <h1 style={styles.title}>비밀번호 재설정</h1>
      <p style={styles.subtitle}>가입 이메일로 인증번호를 보내드립니다.</p>
      {error && <div style={styles.errorBox}>{error}</div>}
      {resendInfo && !error && (
        <div
          style={{
            fontSize: 13,
            color: "#188038",
            margin: "0 0 10px",
            textAlign: "center",
          }}
        >
          {resendInfo}
        </div>
      )}
      <div style={styles.inputGroup}>
        <input
          type="email"
          value={userEmail}
          onChange={(e) => handleEmailInput(e.target.value)}
          placeholder="이메일*"
          style={styles.input}
          disabled={loading || resetOtpSent}
        />
        {!resetOtpSent ? (
          <button
            onClick={handleSendResetOtp}
            disabled={loading}
            style={styles.primaryBtn}
          >
            {loading ? "처리 중..." : "인증번호 받기"}
          </button>
        ) : (
          <>
            <input
              type="text"
              inputMode="numeric"
              value={resetOtp}
              onChange={(e) => setResetOtp(e.target.value.trim())}
              placeholder="인증번호 6자리*"
              style={styles.input}
              disabled={loading}
            />
            <input
              type="password"
              autoComplete="new-password"
              value={resetPw1}
              onChange={(e) => setResetPw1(e.target.value)}
              placeholder="새 비밀번호* (8자 이상)"
              style={styles.input}
              disabled={loading}
            />
            <input
              type="password"
              autoComplete="new-password"
              value={resetPw2}
              onChange={(e) => setResetPw2(e.target.value)}
              placeholder="새 비밀번호 확인*"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleResetPassword();
              }}
              style={styles.input}
              disabled={loading}
            />
            <button
              onClick={handleResetPassword}
              disabled={loading}
              style={styles.primaryBtn}
            >
              {loading ? "처리 중..." : "비밀번호 재설정"}
            </button>
            <button
              type="button"
              onClick={handleSendResetOtp}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                color: "#1a73e8",
                cursor: "pointer",
                fontSize: 12,
                textDecoration: "underline",
                padding: 0,
              }}
            >
              인증번호 다시 받기
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => {
            setStep("login");
            setError("");
            setResendInfo("");
            setResetOtpSent(false);
          }}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: 12,
            padding: 0,
            marginTop: 4,
          }}
        >
          ← 로그인으로 돌아가기
        </button>
      </div>
    </>
  );

  // ── 로그인 화면 ──
  const renderLogin = () => (
    <>
      <h1 style={styles.title}>다시 만나서 반갑습니다.</h1>
      <p style={styles.subtitle}>
        {isCompany
          ? "이메일, 업체코드를 입력해주세요"
          : "이메일을 입력해주세요"}
      </p>

      {error && <div style={styles.errorBox}>{error}</div>}
      {infoMsg && !error && (
        <div
          style={{
            fontSize: 13,
            color: "#188038",
            background: "#e6f4ea",
            border: "1px solid #ceead6",
            borderRadius: 8,
            padding: "10px 12px",
            marginBottom: 12,
            textAlign: "center",
          }}
        >
          {infoMsg}
        </div>
      )}

      {/* <form> 시맨틱 — 브라우저 비밀번호 관리자가 "저장" 을 제안하려면 form
          submit 이벤트가 필요 (CielHS 식 자동 채움의 전제). onClick 만으로는
          저장 프롬프트가 안 떠서 사용자가 매번 비밀번호를 타이핑하게 됨. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAuthEmail();
        }}
        style={{ margin: 0 }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginBottom: "12px",
          }}
        >
          {isCompany && (
            <input
              ref={groupIdRef}
              type="text"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // form submit 막고 다음 필드로
                  userEmailRef.current?.focus();
                }
              }}
              placeholder="업체코드*"
              style={styles.input}
              disabled={loading}
            />
          )}
          <input
            ref={userEmailRef}
            type={isCompany ? "text" : "email"}
            name="email"
            autoComplete="username"
            value={userEmail}
            onChange={(e) => handleEmailInput(e.target.value)}
            placeholder={isCompany ? "사용자 아이디*" : "이메일*"}
            style={styles.input}
            disabled={loading}
          />
          {!isCompany && userEmail && !userEmailValid && (
            <span style={{ fontSize: "13px", color: "#dc2626" }}>
              이메일 형식이 올바르지 않습니다
            </span>
          )}
          {/* 개인 모드 — otpMode 아니면 PW 입력 표시 (CielHS 풍 편의) */}
          {!isCompany && !otpMode && (
            <div style={{ position: "relative" }}>
              <input
                type={showPwLogin ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호* (8자 이상)"
                style={{
                  ...styles.input,
                  paddingRight: 40,
                  width: "100%",
                  boxSizing: "border-box",
                }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPwLogin(!showPwLogin)}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: "2px 4px",
                }}
                title={showPwLogin ? "숨기기" : "보기"}
              >
                {showPwLogin ? "숨김" : "보기"}
              </button>
            </div>
          )}
        </div>

        <button type="submit" disabled={loading} style={styles.primaryBtn}>
          {loading
            ? "처리 중..."
            : isCompany
              ? "로그인"
              : otpMode
                ? "인증 코드 받기"
                : "로그인"}
        </button>
      </form>

      {/* OTP fallback toggle — 비밀번호 잊었거나 OTP 원할 때 */}
      {!isCompany && (
        <div
          style={{
            textAlign: "center",
            margin: "10px 0 4px",
            fontSize: "12px",
            color: "#6b7280",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setOtpMode(!otpMode);
              setError("");
            }}
            style={{
              background: "none",
              border: "none",
              color: "#1a73e8",
              cursor: "pointer",
              fontSize: "12px",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            {otpMode
              ? "← 비밀번호로 로그인"
              : "비밀번호 모르세요? 인증코드로 로그인"}
          </button>
          {!otpMode && (
            <>
              <span style={{ margin: "0 6px", color: "#d1d5db" }}>·</span>
              <button
                type="button"
                onClick={() => {
                  setStep("reset");
                  setError("");
                  setInfoMsg("");
                  setResendInfo("");
                  setResetOtpSent(false);
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1a73e8",
                  cursor: "pointer",
                  fontSize: "12px",
                  textDecoration: "underline",
                  padding: 0,
                }}
              >
                비밀번호 재설정
              </button>
            </>
          )}
        </div>
      )}

      {/* 로그인 유지 체크박스 */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          margin: "4px 0 4px",
          cursor: "pointer",
          fontSize: "13px",
          color: "#6b7280",
        }}
      >
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          style={{
            width: "16px",
            height: "16px",
            cursor: "pointer",
            accentColor: "#18181b",
          }}
        />
        자동 로그인
      </label>
      {/* 기업 모드 토글 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "8px",
          margin: "4px 0 8px",
        }}
      >
        <span style={{ fontSize: "13px", color: "#6b7280" }}>기업 회원</span>
        <div
          onClick={() => {
            setIsCompany(!isCompany);
            setError("");
          }}
          style={{
            width: "40px",
            height: "22px",
            borderRadius: "11px",
            cursor: "pointer",
            background: isCompany ? "#18181b" : "#d1d5db",
            position: "relative",
            transition: "background 0.2s",
          }}
        >
          <div
            style={{
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              background: "#fff",
              position: "absolute",
              top: "2px",
              left: isCompany ? "20px" : "2px",
              transition: "left 0.2s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            }}
          />
        </div>
      </div>

      <div style={styles.links}>
        <span style={{ color: "#9ca3af" }}>계정이 없으신가요?</span>
        <button
          onClick={() => onNavigate && onNavigate("signup")}
          style={styles.linkBtn}
        >
          회원가입
        </button>
      </div>

      {/* Divider + Social login — 서버에 키 구성된 provider 만 표시 */}
      {oauthProviders &&
        (oauthProviders.google ||
          oauthProviders.naver ||
          oauthProviders.kakao) && (
          <>
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>또는</span>
              <div style={styles.dividerLine} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              {oauthProviders.google && (
                <button
                  onClick={handleGoogleLogin}
                  style={styles.socialBtn}
                  disabled={loading}
                >
                  <span
                    style={{
                      ...styles.socialIcon,
                      background: "#fff",
                      border: "1px solid #dadce0",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  </span>
                  <span>구글 로그인</span>
                </button>
              )}

              {oauthProviders.naver && (
                <button
                  onClick={handleNaverLogin}
                  style={styles.socialBtn}
                  disabled={loading}
                >
                  <span
                    style={{
                      ...styles.socialIcon,
                      background: "#03C75A",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    N
                  </span>
                  <span>네이버 로그인</span>
                </button>
              )}

              {oauthProviders.kakao && (
                <button
                  onClick={handleKakaoLogin}
                  style={styles.socialBtn}
                  disabled={loading}
                >
                  <span
                    style={{
                      ...styles.socialIcon,
                      background: "#FEE500",
                      color: "#3C1E1E",
                      fontWeight: "bold",
                      fontSize: "11px",
                    }}
                  >
                    TALK
                  </span>
                  <span>카카오 로그인</span>
                </button>
              )}
            </div>
          </>
        )}
    </>
  );

  // ── 인증코드 입력 화면 ──
  const renderAuth = () => (
    <>
      <h1 style={{ ...styles.title, fontSize: "28px" }}>로그인</h1>
      <p style={styles.subtitle}>인증 코드를 입력해주세요</p>

      {error && <div style={styles.errorBox}>{error}</div>}
      {resendInfo && (
        <div
          style={{
            background: "#ecfdf5",
            color: "#065f46",
            border: "1px solid #6ee7b7",
            borderRadius: "8px",
            padding: "10px 12px",
            fontSize: "13px",
            marginBottom: "10px",
            textAlign: "center",
          }}
        >
          {resendInfo}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            type="text"
            maxLength={1}
            value={authNum[i] || ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              if (!val && e.nativeEvent.inputType === "deleteContentBackward") {
                const newAuth = authNum.slice(0, i) + authNum.slice(i + 1);
                setAuthNum(newAuth);
                if (i > 0) e.target.previousSibling?.focus();
                return;
              }
              const newAuth = authNum.slice(0, i) + val + authNum.slice(i + 1);
              setAuthNum(newAuth.slice(0, 6));
              if (val && i < 5) e.target.nextSibling?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !authNum[i] && i > 0)
                e.target.previousSibling?.focus();
              if (e.key === "Enter" && authNum.length === 6) handleOtpLogin();
            }}
            autoFocus={i === 0}
            style={styles.otpInput}
          />
        ))}
      </div>

      <p
        style={{
          textAlign: "center",
          fontSize: "13px",
          color: "#6b7280",
          marginBottom: "16px",
        }}
      >
        전송된 6자리 코드를 입력하세요.
      </p>

      <button
        onClick={handleOtpLogin}
        disabled={loading}
        style={{ ...styles.primaryBtn, marginBottom: "8px" }}
      >
        {loading ? "처리 중..." : "확인"}
      </button>
      <button
        onClick={handleResendOtp}
        disabled={loading || resendCooldown > 0}
        style={{
          ...styles.outlineBtn,
          marginBottom: "8px",
          opacity: resendCooldown > 0 ? 0.5 : 1,
        }}
      >
        {resendCooldown > 0
          ? `재전송 가능 (${resendCooldown}초)`
          : "인증번호 재전송"}
      </button>
      <button
        onClick={() => {
          setStep("login");
          setAuthNum("");
          setError("");
          setResendInfo("");
          setResendCooldown(0);
        }}
        style={{ ...styles.outlineBtn, marginBottom: "8px" }}
      >
        뒤로가기
      </button>

      <div style={styles.links}>
        <span style={{ color: "#9ca3af" }}>계정이 없으신가요?</span>
        <button
          onClick={() => onNavigate && onNavigate("signup")}
          style={styles.linkBtn}
        >
          회원가입
        </button>
      </div>
    </>
  );

  // ── Phase 4 — PW 설정 화면 (OTP 로그인 후 PW 미설정 사용자) ──
  const handleSetPassword = async () => {
    setError("");
    if (!newPw1 || newPw1.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (newPw1 !== newPw2) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/set-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${pwSetup.token}`,
        },
        body: JSON.stringify({
          newPassword: newPw1,
          newPasswordConfirm: newPw2,
        }),
      });
      const data = await res.json();
      if (data.success) {
        onLoginSuccess && onLoginSuccess(pwSetup.user, pwSetup.token);
      } else {
        setError(data.message || "비밀번호 설정에 실패했습니다.");
      }
    } catch (e) {
      setError("비밀번호 설정 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordSetup = () => (
    <>
      <h1 style={styles.title}>비밀번호를 설정하세요</h1>
      <p style={styles.subtitle}>
        다음 로그인부터 이메일 + 비밀번호로 빠르게 진입할 수 있어요
      </p>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        <input
          type="password"
          name="new-password"
          autoComplete="new-password"
          value={newPw1}
          onChange={(e) => setNewPw1(e.target.value)}
          placeholder="새 비밀번호* (8자 이상)"
          style={styles.input}
          disabled={loading}
        />
        <input
          type="password"
          name="new-password-confirm"
          autoComplete="new-password"
          value={newPw2}
          onChange={(e) => setNewPw2(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSetPassword();
          }}
          placeholder="비밀번호 확인*"
          style={styles.input}
          disabled={loading}
        />
      </div>
      <button
        onClick={handleSetPassword}
        disabled={loading}
        style={styles.primaryBtn}
      >
        {loading ? "처리 중..." : "비밀번호 설정 + 로그인"}
      </button>
      <div
        style={{
          textAlign: "center",
          margin: "10px 0",
          fontSize: "12px",
          color: "#6b7280",
        }}
      >
        <button
          type="button"
          onClick={() =>
            onLoginSuccess && onLoginSuccess(pwSetup.user, pwSetup.token)
          }
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: "12px",
            textDecoration: "underline",
            padding: 0,
          }}
        >
          나중에 설정 — 일단 로그인
        </button>
      </div>
    </>
  );

  return (
    <div
      style={{
        ...styles.page,
        // 세로 제약 시(폰/짧은 화면): 카드를 위에서부터(세로 상단) 흘려 스크롤 + 고정 푸터 높이만큼 하단 여백.
        //   (page 는 flexDirection:row → alignItems 가 세로축, justifyContent 가 가로축)
        // 데스크톱(여유 높이): 세로 가운데 유지하되 짧은 푸터를 피하도록 약간의 하단 여백만.
        ...(constrained
          ? {
              alignItems: "flex-start",
              paddingTop: 16,
              paddingBottom: isMobile ? 200 : 150,
            }
          : { paddingBottom: 96 }),
        // 좁은 폭(폰/태블릿): 카드가 화면 가장자리에 붙지 않게 가로 여백 + 100vw 넘침 방지.
        ...(isMobile || isTablet
          ? { paddingLeft: 16, paddingRight: 16, boxSizing: "border-box" }
          : {}),
      }}
    >
      {loading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.spinner} />
        </div>
      )}

      <div
        style={{
          ...styles.card,
          // 폰: 좁은 폭에 맞춰 좌우 패딩 축소(콘텐츠 폭 확보).
          ...(isMobile ? { padding: "28px 20px", borderRadius: 14 } : {}),
        }}
      >
        {/* 브랜드 헤더 — 도메인별 워드마크(loginhs.* → LoginHS, 그 외 → DocuAI).
            마케팅 슬로건은 로그인 폼에서 제거해 산만함 축소. */}
        <div style={styles.brand}>
          <span style={styles.brandName}>
            {isLoginHSProduct() ? "LoginHS" : "DocuAI"}
          </span>
        </div>
        <div style={styles.cardInner}>
          {pwSetup
            ? renderPasswordSetup()
            : step === "reset"
              ? renderPasswordReset()
              : step === "login"
                ? renderLogin()
                : renderAuth()}
        </div>
      </div>

      {sessionLimit && (
        <SessionLimitModal
          activeSessions={sessionLimit.activeSessions}
          onCancel={() => setSessionLimit(null)}
          onProceed={handleSessionLimitProceed}
        />
      )}

      <Footer />
    </div>
  );
};

/* ── Inline Styles ── */
const styles = {
  page: {
    minHeight: "100vh",
    width: "100vw",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // 앱 내부 테마(warm cream + Google blue accent)와 통일 — 크림→연블루 그라데이션
    background: "linear-gradient(160deg, #fafaf8 0%, #eef2fb 100%)",
    fontFamily:
      "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    minWidth: 0, // 좁은 단말(<420px)에서 가로 넘침 방지 — width:100% + maxWidth 로 유동
    maxWidth: "450px",
    width: "100%",
    padding: "36px 32px",
    borderRadius: "16px",
    background: "#ffffff",
    border: "1px solid #e8eaed",
    boxShadow: "0 10px 36px rgba(20,30,60,0.09)",
  },
  // 브랜드 헤더 — DocuAI 워드마크 + 슬로건(블루 accent)
  brand: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "5px",
    marginBottom: "20px",
  },
  brandName: {
    // 로고(사이드바·랜딩) 워드마크와 동일 — Montserrat 700 (페이지 Pretendard 덮어쓰기 해제)
    fontFamily:
      "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: "22px",
    fontWeight: 700,
    color: "#141413",
    letterSpacing: "-0.02em",
  },
  brandSlogan: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1a73e8",
    letterSpacing: "-0.2px",
  },
  cardInner: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "8px",
    color: "#1a1b1e",
  },
  subtitle: {
    fontSize: "18px",
    color: "#6b7280",
    textAlign: "center",
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  primaryBtn: {
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: "8px",
    background: "#18181b",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
    marginBottom: "8px",
  },
  outlineBtn: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#fff",
    color: "#374151",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
  },
  errorBox: {
    padding: "10px 14px",
    borderRadius: "8px",
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    fontSize: "14px",
    marginBottom: "12px",
    textAlign: "center",
  },
  links: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "14px",
    marginBottom: "12px",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
    padding: 0,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    background: "#e5e7eb",
  },
  dividerText: {
    fontSize: "12px",
    color: "#9ca3af",
    textTransform: "uppercase",
  },
  socialBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "10px",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    background: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
    transition: "background 0.15s",
    position: "relative",
  },
  socialIcon: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    left: "14px",
  },
  otpInput: {
    width: "46px",
    height: "52px",
    textAlign: "center",
    fontSize: "22px",
    fontWeight: "600",
    border: "2px solid #e5e7eb",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
  },
  loadingOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(255,255,255,0.75)",
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #e5e7eb",
    borderTopColor: "#18181b",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

// Inject spinner animation
if (typeof document !== "undefined") {
  if (!document.getElementById("login-spinner-style")) {
    const s = document.createElement("style");
    s.id = "login-spinner-style";
    s.textContent = "@keyframes spin { to { transform: rotate(360deg); } }";
    document.head.appendChild(s);
  }
}

export default Login;
